import React, { useLayoutEffect } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import OrderStatusDropdown from "./GroupByDropdown/OrderStatusDropdown";

const TopDirections = ({ item, isExpeditor = false, isCompleted = true, setIsCompleted }) => {
  // const { isMobileView } = useScreenSize();
  const { t } = useTranslation();

  useLayoutEffect(() => {
    const root = am5.Root.new("top-routes");
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo?.dispose();

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: root.verticalLayout,
        innerRadius: 0,
      }),
    );

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: "count",
        categoryField: "label",
        alignLabels: false,
        legendLabelText: "{category}",
        legendValueText: "{value.percent.formatNumber('#.0')}%",
      }),
    );

    series.labels.template.setAll({
      text: "{percent}%",
      inside: true,
      centerX: am5.p50,
      centerY: am5.p50,
      fill: am5.color(0xffffff),
      fontSize: 10,
      fontFamily: "Inter",
      fontWeight: "400",
    });

    series.ticks.template.set("visible", false);

    const colors = [
      "#1E40A1",
      "#4064CA",
      "#4E7CFF",
      "#22C0FF",
      "#80DBFF",
      "#FFB5CE",
      "#FF98BB",
      "#D7ACFF",
      "#A471D3",
      "#8555B1",
    ];

    const total = item.reduce((sum, i) => sum + i.count, 0);

    const formattedData = item
      .filter((i) => i.routeName)
      .map((i, index) => {
        const [from, to] = i.routeName.split(" - ").map((s) => s.trim());
        const percent = ((i.count / total) * 100).toFixed(1);
        const colorHex = colors[index % colors.length];
        return {
          label: `${from} → ${to}`,
          fullRoute: i.routeName,
          count: i.count,
          percent,
          color: am5.color(colorHex),
          rawColor: colorHex,
        };
      });

    series.data.setAll(formattedData);

    series.labels.template.set("forceHidden", true);

    series.events.once("datavalidated", () => {
      series.slices.each((slice) => {
        const dataItem = slice.dataItem;
        if (!dataItem) return;

        const percent = dataItem.get("valuePercentTotal").toFixed(1);
        if (percent < 3) return;

        const startAngle = slice.get("startAngle");
        const arc = slice.get("arc");
        const radius = slice.get("radius");
        const innerRadius = slice.get("innerRadius");

        const midAngle = startAngle + arc / 2;
        const radians = (midAngle * Math.PI) / 180;

        const labelRadius = innerRadius + (radius - innerRadius) * 0.6;

        const x = Math.cos(radians) * labelRadius;
        const y = Math.sin(radians) * labelRadius;

        const label = am5.Label.new(series._root, {
          text: `${percent}%`,
          x,
          y,
          centerX: am5.p50,
          centerY: am5.p50,
          populateText: true,
          fontSize: 10,
          fontFamily: "Inter",
          fontWeight: "400",
          fill: am5.color(0xffffff),
        });

        series.labelsContainer.children.push(label);
      });
    });

    series.slices.template.adapters.add("fill", (fill, target) => {
      const dataContext = target.dataItem?.dataContext;
      if (dataContext && dataContext.color) {
        return dataContext.color;
      }
      return fill;
    });

    series.onPrivate("valueLow", () => {
      series.slices.each((slice, index) => {
        const dataItem = slice.dataItem;
        if (dataItem && dataItem.dataContext && dataItem.dataContext.color) {
          slice.set("fill", dataItem.dataContext.color);
        }
      });
    });

    series.slices.template.setAll({
      strokeOpacity: 0,
      strokeWidth: 0,
    });

    return () => root.dispose();
  }, [item]);

  const getTotal = () => {
    return item.reduce((sum, i) => sum + i.count, 0);
  };

  const getTopColors = () => [
    "#1E40A1",
    "#4064CA",
    "#4E7CFF",
    "#22C0FF",
    "#80DBFF",
    "#FFB5CE",
    "#FF98BB",
    "#D7ACFF",
    "#A471D3",
    "#8555B1",
  ];

  const formattedList = item
    .filter((i) => i.routeName)
    .map((i, index) => {
      const [from, to] = i.routeName.split(" - ").map((s) => s.trim());
      const percent = ((i.count / getTotal()) * 100).toFixed(1);
      return {
        label: `${from} — ${to}`,
        percent,
        color: getTopColors()[index % 10],
        count: i.count
      };
    });

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{t("analytics.topRoutes")}</span>
        {isExpeditor && <OrderStatusDropdown isCompleted={isCompleted} setIsCompleted={setIsCompleted} />}
      </div>
      <div
        className={style.area}
        style={{ display: "flex", alignItems: "center", flexDirection: "row-reverse" }}
      >
        <div
          style={{
            width: "max-content",
            display: "flex",
            flexDirection: "column",
            gap: "13px",
            paddingRight: "40px",
          }}
        >
          {formattedList.map((item, idx) => (
            <div
              key={idx}
              style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "space-between" }}
            >
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    backgroundColor: item.color,
                    borderRadius: "2px",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    color: "#5A5959",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: "400",
                  }}
                >
                  {item.label}
                </span>
              </div>
              <span
                style={{
                  color: "#5A5959",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontWeight: "400",
                }}
              >
                {`${item.percent}% (${item.count})`}
              </span>
            </div>
          ))}
        </div>
        
        <div id="top-routes" className={style.area_chart} style={{ width: "100%", height: "100%" }}></div>
      </div>
    </div>
  );
};

export default TopDirections;
